import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText'];

  /**
   * CRO fixes Oct 2026: the bubble text holds a visually-hidden label plus the
   * visible count, so read and write the count span only. Reading the whole
   * text returned NaN and left an empty black dot after adding to cart.
   * @returns {HTMLElement}
   */
  get countElement() {
    return this.refs.cartBubbleText.querySelector('.cart-bubble__text-count') ?? this.refs.cartBubbleText;
  }

  /** @type {number} */
  get currentCartCount() {
    const value = parseInt((this.countElement.textContent ?? '').trim(), 10);
    return Number.isNaN(value) ? 0 : value;
  }

  set currentCartCount(value) {
    const safeValue = Number.isNaN(value) ? 0 : value;
    this.countElement.textContent = safeValue < 100 ? String(safeValue) : '';

    const label = this.refs.cartBubbleText.querySelector('.visually-hidden');
    if (label) label.textContent = label.textContent.replace(/\d+\s*$/, String(safeValue));
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count
    const newCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    this.refs.cartBubble.classList.toggle('visually-hidden', newCount === 0);
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', newCount > 0 && animate);
    this.currentCartCount = newCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', newCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');
    const visibleCount = (this.countElement.textContent ?? '').trim();
    if (sessionStorageCount === null) return;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);
      if (value === visibleCount) return;

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
